import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { FilterBar } from '@client/components/filters';
import {
    type ObligationReassignmentAction,
    ObligationReassignmentDrawer,
} from '@client/components/obligations/ObligationReassignmentDrawer';
import { ObligationStatusUpdateModal } from '@client/components/obligations/ObligationStatusUpdateModal';
import { useObligationFilterConfig } from '@client/components/obligations/useObligationFilterConfig';
import {
    type BuiltInTableView,
    builtInViewPayload,
    TableViewsBar,
    useTableViews,
    type ViewParamsSpec,
} from '@client/components/table-views';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE, OBLIGATION_STATUSES_KEYS } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useObligationColumns } from './columns';

/**
 * Three fixed views for the obligations register (CUR-386), served by the saved-views engine. The
 * latest/all split is a non-filter query param (`latestPerFamily`, a special server-side behaviour —
 * see `buildObligationListWhere`), declared via the params spec so views can carry it.
 *  - Latest version (default): the engine's built-in "All" tab, relabelled — the *neutral* view is
 *    the current version of every obligation family, so the params spec defaults carry
 *    `latestPerFamily: true`. Making it the "All" tab also puts it first and makes it the fresh-load
 *    default, matching the ticket's required order without a starred saved view.
 *  - All versions: built-in preset switching `latestPerFamily` off (every version of every family).
 *  - Awaiting review: built-in preset — an ordinary status filter, all versions.
 * User-saved views sit on top of these; the built-ins always render.
 */
const OBLIGATION_VIEW_PARAMS: ViewParamsSpec = {
    schema: z.object({ latestPerFamily: z.boolean() }),
    defaults: { latestPerFamily: true },
};

const OBLIGATION_BUILT_IN_VIEWS: BuiltInTableView[] = [
    {
        id: '__all-versions__',
        name: 'All versions',
        description: 'Every version of every obligation',
        icon: 'folder',
        payload: builtInViewPayload([], { latestPerFamily: false }),
    },
    {
        id: '__awaiting-review__',
        name: 'Awaiting review',
        description: 'Obligation versions awaiting review',
        icon: 'clock',
        payload: builtInViewPayload(
            [
                {
                    id: 'awaiting-review-status',
                    filterId: 'status',
                    operator: 'in',
                    value: [
                        OBLIGATION_STATUSES_KEYS.AWAITING_REVIEW,
                        OBLIGATION_STATUSES_KEYS.NEW_VERSION_AWAITING_REVIEW,
                    ],
                },
            ],
            { latestPerFamily: false },
        ),
    },
];

const ObligationSearchSchema = z.object({
    'obligation-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
    /** Saved-view deep link (same contract as claims/incidents). */
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/compliance/obligations/')({
    staticData: {
        ui: {
            title: 'Obligations',
            subtitle: 'Create, track, and manage obligations',
        },
    },
    validateSearch: zodValidator(ObligationSearchSchema),
    component: ObligationSearch,
});

function ObligationSearch() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();
    const [search, setSearch] = useState(['']);
    const [statusUpdateModalVisible, setStatusUpdateModalVisible] = useState(false);
    const [reassignmentAction, setReassignmentAction] = useState<ObligationReassignmentAction>(['none']);

    const { config: filterConfig, ready: filterConfigReady } = useObligationFilterConfig();

    // NOTE: the path-state key must be 'query-params' — useTableViews' return-visit detection
    // (hasStoredPathState) looks for exactly that key.
    const [queryParams, setQueryParams] = usePathState<RouterInputs['obligation']['listObligations']>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
        where: {},
        // Matches the params-spec defaults (the "Latest version" tab): one row per obligation
        // family. The shared ObligationsTableDrawer used by Controls + Incidents does NOT opt in,
        // so its callers see all versions as before.
        latestPerFamily: true,
    });

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/params/sort instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isPending, refetch } = trpc.obligation.listObligations.useQuery(queryParams, {
        enabled: viewsReady,
    });

    const columns = useObligationColumns({
        search,
    });

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isPending,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: { position: 'both', maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS },
        columnVisibility: {
            enabled: true,
            persistKey: 'obligations-table-columns',
            enableReordering: true,
            defaultHidden: ['section'],
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
        },
    });

    const views = useTableViews({
        entityType: 'OBLIGATION',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'obligation-filters',
        builtInViews: OBLIGATION_BUILT_IN_VIEWS,
        builtinAllLabel: 'Latest version',
        viewParams: OBLIGATION_VIEW_PARAMS,
        initialFilters: searchParams['obligation-filters'],
        initialActiveViewId: searchParams.view,
        queryParams,
        setQueryParams,
        table: tableProps,
        columnPersistKey: 'obligations-table-columns',
        defaultOrderBy: { id: 'desc' } as const,
        defaultPageSize: DEFAULT_PAGE_SIZE,
        defaultHidden: ['section'],
        onResolved: () => setViewsReady(true),
    });

    // Search-term highlight for the description/code columns. Derived from the engine's filter
    // state post-render (the columns feed usePrismaTable, which the engine needs — so the terms
    // can't be read before the hook runs). Same one-render lag as the old onChange wiring.
    // Content-equal terms bail out (return prev) — searchTerms' identity is not stable across
    // renders (its memo keys on the filter config, which rebuilds with its query data), and an
    // unconditional set would re-render in a loop.
    const searchTerms = views.filterState.searchTerms;
    useEffect(() => {
        const next = searchTerms && searchTerms.length > 0 ? searchTerms : [''];
        setSearch((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
    }, [searchTerms]);

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />

            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    <>
                        <Tooltip
                            title={
                                tableProps.selectedRowKeys.length > 0
                                    ? `Reassign ${tableProps.selectedRowKeys.length} selected obligation${tableProps.selectedRowKeys.length > 1 ? 's' : ''}`
                                    : 'Select obligations to reassign'
                            }
                        >
                            <Button
                                type="secondary"
                                disabled={!tableProps.selectedRowKeys.length}
                                onClick={() =>
                                    setReassignmentAction([
                                        'selected',
                                        (tableProps.selectedRowKeys as (string | number)[]).map((k) =>
                                            BigInt(String(k)),
                                        ),
                                    ])
                                }
                            >
                                Reassign selected obligations
                                {tableProps.selectedRowKeys.length > 0 ? ` (${tableProps.selectedRowKeys.length})` : ''}
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                tableProps.selectedRowKeys.length > 0
                                    ? `Update status for ${tableProps.selectedRowKeys.length} selected obligation${tableProps.selectedRowKeys.length > 1 ? 's' : ''}`
                                    : 'Select obligations to update their status'
                            }
                        >
                            <Button
                                type="secondary"
                                disabled={!tableProps.selectedRowKeys.length}
                                onClick={() => setStatusUpdateModalVisible(true)}
                            >
                                Update status
                                {tableProps.selectedRowKeys.length > 0 ? ` (${tableProps.selectedRowKeys.length})` : ''}
                            </Button>
                        </Tooltip>
                        <Button.Add.Link
                            to="/console/$orgId/compliance/obligations/add"
                            params={{ orgId }}
                            variant="primary"
                            label="New obligation"
                        />
                    </>
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/compliance/obligations/details/$id/manage',
                                params: {
                                    orgId,
                                    id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />

            <ObligationStatusUpdateModal
                open={statusUpdateModalVisible}
                selectedObligationIds={tableProps.selectedRowKeys as bigint[]}
                onClose={() => setStatusUpdateModalVisible(false)}
                onSuccess={() => {
                    tableProps.clearSelection();
                    refetch();
                }}
            />

            <ObligationReassignmentDrawer
                action={reassignmentAction}
                onClose={(resetSelectedRowKeys) => {
                    setReassignmentAction(['none']);

                    if (resetSelectedRowKeys) {
                        tableProps.clearSelection();
                    }
                }}
            />
        </PageController>
    );
}
